import type { Annotation } from "@/types/annotation";
import { Prediction } from "@/utils/api/inference";

export const DEFAULT_CONFIDENCE_THRESHOLD = 0.5;

export const predictionToAnnotation = (prediction: Prediction, frameId: string): Annotation => {
    const {
        x,
        y,
        width,
        height,
        detection_id: id,
        class: classLabel,
        thumbnailDataUrl,
        fileId,
        confidence,
        masks,
        type
    } = prediction;
    return {
        id,
        x,
        y,
        width,
        height,
        label: classLabel.replace(/\s*\(\d+%\)$/, ""),
        draft: false,
        thumbnailDataUrl,
        fileId,
        frameId,
        confidence,
        masks,
        type
    };
};

/**
 * OwlVIT returns a confidence score between 0 and 1, but the model is super sensitive and it usually works great
 * within 0.95 +- 0.02 - so we have this heuristic to adjust the confidence score to a score between 0 and 100.
 * @param confidence - The confidence score from OwlVIT
 * @returns The adjusted confidence score
 */
const adjustedConfidenceFromOwlvit = (confidence: number) => {
    // Less than 0.75 the model gives super non-meaningful results
    // So we adjust the confidence score to a score between 0 and 100
    return (confidence - 0.75) / 0.25;
};

/**
 * We want to adjust the confidence score to a score between 0 and 1
 * @param adjustedConfidence - The adjusted confidence score
 * @returns The confidence score from OwlVIT
 */
const adjustedConfidenceToOwlvit = (adjustedConfidence: number) => {
    // We want to adjust the confidence score to a score between 0 and 1
    return adjustedConfidence * 0.25 + 0.75;
};

export { adjustedConfidenceFromOwlvit, adjustedConfidenceToOwlvit };

/**
 * Box prompt item for OWLv2 inference
 */
export interface BoxPromptItem {
    x: number;
    y: number;
    w: number;
    h: number;
    negative: boolean;
    cls: string;
}

/**
 * Box prompting data for OWLv2 inference
 */
export interface BoxPromptingData {
    file_uuid: string;
    width: number;
    height: number;
    boxes: BoxPromptItem[];
}

/**
 * Options for building box prompting data
 */
export interface BuildBoxPromptingDataOptions {
    /** Filter annotations to only include this class. If not provided, includes all classes. */
    classFilter?: string;
    /** Workspace URL prefix for file_uuid. Defaults to "anonymous". */
    workspaceUrl?: string;
}

/**
 * Builds box prompting data from annotations for OWLv2 inference.
 * This is used for "Find Similar Objects" functionality.
 *
 * @param annotations - Array of annotations with frameId and fileId
 * @param mediaDimensions - Map of frameId to {width, height} dimensions
 * @param options - Optional configuration for filtering and formatting
 * @returns Array of BoxPromptingData ready for inference
 */
export const buildBoxPromptingData = (
    annotations: (Annotation & { frameId: string; fileId: string })[],
    mediaDimensions: Map<string, { width: number; height: number }>,
    options: BuildBoxPromptingDataOptions = {}
): BoxPromptingData[] => {
    const { classFilter, workspaceUrl = "anonymous" } = options;

    // Filter to only include confirmed annotations (not drafts or empty)
    let validAnnotations = annotations.filter(
        (anno) => !anno.draft && !anno.empty && anno.width > 0 && anno.height > 0
    );

    // Apply class filter if provided
    if (classFilter) {
        validAnnotations = validAnnotations.filter((anno) => anno.label === classFilter);
    }

    if (validAnnotations.length === 0) {
        return [];
    }

    // Group annotations by frameId
    const annotationsByFrame = new Map<
        string,
        (Annotation & { frameId: string; fileId: string })[]
    >();
    validAnnotations.forEach((anno) => {
        const frameId = anno.frameId;
        if (!annotationsByFrame.has(frameId)) {
            annotationsByFrame.set(frameId, []);
        }
        annotationsByFrame.get(frameId)!.push(anno);
    });

    // Build prompting data for each frame
    const boxPromptingData: BoxPromptingData[] = [];

    annotationsByFrame.forEach((frameAnnotations, frameId) => {
        // Get dimensions for this frame
        const dimensions = mediaDimensions.get(frameId);
        if (!dimensions) {
            console.warn(`[buildBoxPromptingData] No dimensions found for frame ${frameId}`);
            return;
        }

        boxPromptingData.push({
            file_uuid: `${workspaceUrl}/${frameId}`,
            width: dimensions.width,
            height: dimensions.height,
            boxes: frameAnnotations.map((anno) => ({
                x: Math.round(anno.x),
                y: Math.round(anno.y),
                w: Math.round(anno.width),
                h: Math.round(anno.height),
                negative: false,
                cls: anno.label
            }))
        });
    });

    return boxPromptingData;
};

/**
 * Calculates a dynamic confidence threshold based on the number of prompts.
 * More prompts = higher threshold (model gets more "creative"/hallucinates with more examples).
 *
 * Note: These thresholds are in the ADJUSTED confidence scale (0-1 after adjustedConfidenceFromOwlvit).
 * For box prompting, we use higher thresholds than text prompting because visual prompts
 * tend to produce more false positives.
 *
 * Adjusted scale to raw OWLv2 confidence mapping:
 * - 0.6 adjusted = 0.90 raw (minConfidence with 1 prompt)
 * - 0.96 adjusted = 0.99 raw (maxConfidence with 20+ prompts)
 *
 * Heuristic: linear progression from minConfidence (1 prompt) to maxConfidence (maxPrompts+ prompts)
 *
 * @param promptCount - Number of box prompts being used
 * @param minConfidence - Minimum confidence threshold in adjusted scale (default: 0.6, = 0.90 raw)
 * @param maxConfidence - Maximum confidence threshold in adjusted scale (default: 0.96, = 0.99 raw)
 * @param maxPrompts - Number of prompts at which max confidence is reached (default: 20)
 * @returns Confidence threshold value between minConfidence and maxConfidence (adjusted scale)
 */
export const calculateBoxPromptConfidenceThreshold = (
    promptCount: number,
    minConfidence: number = 0.6,
    maxConfidence: number = 0.96,
    maxPrompts: number = 20
): number => {
    return minConfidence + (maxConfidence - minConfidence) * Math.min(promptCount / maxPrompts, 1);
};
